import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiWorker } from '../api/worker';
import { MessageBoardTopic, MessageBoardComment } from '../types';
import { useTenant } from '../contexts/TenantContext';
import toast from 'react-hot-toast';

export function useDiscussions() {
  const { node } = useTenant();

  return useQuery({
    queryKey: ['discussions', node?.id],
    queryFn: async () => {
      if (!node?.id) return [];
      const targetId = node.id === 'platform-root' ? 'global' : node.id;
      const response = await apiWorker.get<any>(`/api/nodes/${targetId}/topics`);
      return (response.data && Array.isArray(response.data.content)) ? response.data.content : response.data;
    },
    enabled: !!node?.id,
  });
}

export function useDiscussion(topicId: string) {
  return useQuery({
    queryKey: ['discussion', topicId],
    queryFn: async () => {
      const response = await apiWorker.get<MessageBoardTopic>(`/api/topics/${topicId}`);
      return response.data;
    },
    enabled: !!topicId,
  });
}

export function useDiscussionComments(topicId: string) {
  return useQuery({
    queryKey: ['discussion-comments', topicId],
    queryFn: async () => {
      const response = await apiWorker.get<any>(`/api/topics/${topicId}/comments`);
      return (response.data && Array.isArray(response.data.content)) ? response.data.content : response.data;
    },
    enabled: !!topicId,
  });
}

export function useCreateTopic() {
  const queryClient = useQueryClient();
  const { node } = useTenant();

  return useMutation({
    mutationFn: async (data: Partial<MessageBoardTopic>) => {
      const targetId = node?.id === 'platform-root' ? 'global' : (node?.id || 'global');
      const response = await apiWorker.post(`/api/nodes/${targetId}/topics`, { ...data });
      return (response.data && Array.isArray(response.data.content)) ? response.data.content : response.data;
    },
    onMutate: async (newTopic) => {
      await queryClient.cancelQueries({ queryKey: ['discussions', node?.id] });
      const previousDiscussions = queryClient.getQueryData<MessageBoardTopic[]>(['discussions', node?.id]);

      if (previousDiscussions) {
        const optimisticTopic: MessageBoardTopic = {
          id: `temp-${Date.now()}`,
          nodeId: node?.id || '',
          nodeName: node?.name || '',
          title: newTopic.title || '',
          content: newTopic.content || '',
          authorUserId: 'me',
          authorName: 'Me',
          authorAvatarColor: 'bg-brand-primary',
          isPrivate: false,
          commentCount: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          ...newTopic
        } as MessageBoardTopic;

        queryClient.setQueryData(['discussions', node?.id], [optimisticTopic, ...previousDiscussions]);
      }

      return { previousDiscussions };
    },
    onError: (err, newTopic, context) => {
      if (context?.previousDiscussions) {
        queryClient.setQueryData(['discussions', node?.id], context.previousDiscussions);
      }
      toast.error('Failed to start discussion');
    },
    onSuccess: () => {
      toast.success('Discussion started');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['discussions', node?.id] });
      queryClient.invalidateQueries({ queryKey: ['discussions', node?.id, 'infinite'] });
      queryClient.invalidateQueries({ queryKey: ['discussions'] });
    },
  });
}

export function useCreateComment() {
  const queryClient = useQueryClient();
  const { node } = useTenant();

  return useMutation({
    mutationFn: async ({ topicId, ...data }: any) => {
      const response = await apiWorker.post(`/api/topics/${topicId}/comments`, data);
      return (response.data && Array.isArray(response.data.content)) ? response.data.content : response.data;
    },
    onMutate: async ({ topicId, ...newComment }) => {
      await queryClient.cancelQueries({ queryKey: ['discussion-comments', topicId] });
      const previousComments = queryClient.getQueryData<MessageBoardComment[]>(['discussion-comments', topicId]);

      if (previousComments) {
        const optimisticComment: MessageBoardComment = {
          id: `temp-${Date.now()}`,
          topicId,
          content: newComment.content || '',
          authorUserId: 'me',
          authorName: 'Me',
          authorAvatarColor: 'bg-brand-primary',
          depth: newComment.depth || 0,
          createdAt: new Date().toISOString(),
          ...newComment
        } as MessageBoardComment;

        queryClient.setQueryData(['discussion-comments', topicId], [...previousComments, optimisticComment]);
      }

      return { previousComments };
    },
    onError: (err, variables, context) => {
      if (context?.previousComments) {
        queryClient.setQueryData(['discussion-comments', variables.topicId], context.previousComments);
      }
      toast.error('Failed to post comment');
    },
    onSuccess: () => {
      toast.success('Comment posted');
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({ queryKey: ['discussion-comments', variables.topicId] });
      queryClient.invalidateQueries({ queryKey: ['discussion', variables.topicId] });
      queryClient.invalidateQueries({ queryKey: ['discussions', node?.id] });
      queryClient.invalidateQueries({ queryKey: ['discussions', node?.id, 'infinite'] });
    },
  });
}
